import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { Link } from "react-router-dom";
import loadable from "@loadable/component";
import { useDispatch, useSelector } from "react-redux";
import { useIsMobile } from "../../hooks";
import {
  CURRENCY_SIGN,
  CURRENCY_MAPPING,
  SUPPORTED_REGIONS,
  PRODUCT_VARIANT_ABBREVIATIONS,
  STATUS,
} from "../../../shared/enums";
import {
  selectRegionalPricesData,
  fetchRegionalPrices,
  selectRegionalPricesStatus,
} from "../../features/regionalPrices/regionalPricesSlice";
import {
  selectBannersData,
  selectBannersError,
  selectBannersStatus,
} from "../../features/banners/bannersSlice";
import {
  getFilterPrice,
  getUnitPrice,
  getSocksPrice,
} from "../../features/regionalPrices/regionalPricesHelpers";
import { getAir3BuyUrl } from "../../static-data/shopUrls";
import { getAir3MenuCta } from "./HeaderRefresh.helpers";
const ImageLoader = loadable(() => import("./../common/ImageLoader"));
import { MenuBar, CloseBar, SidebarItem } from "../header";
import logo from "assets/images/logo.svg";
import milaAir3 from "assets/images/landingAir/air3-nav-menu.jpg";
import milaAir3Webp from "assets/images/landingAir/air3-nav-menu.jpg?as=webp";
import milaAirMini from "assets/images/landingAir/mini-nav-menu.jpg";
import milaAirMiniWebp from "assets/images/landingAir/mini-nav-menu.jpg?as=webp";

import accessory01 from "assets/images/landingAir/filter-air3.jpg";
import accessory01Webp from "assets/images/landingAir/filter-air3.jpg?as=webp";

import accessory03 from "assets/images/landingAir/accessory-img-03.jpg";
import accessory03Webp from "assets/images/landingAir/accessory-img-03.jpg?as=webp";

import airminiAccessory from "assets/images/landingAir/filter-mini.jpg";
import airminiAccessoryWebp from "assets/images/landingAir/filter-mini.jpg?as=webp";

const AIR_MINI_WAITLIST_URL = "https://milamarketing.typeform.com/preordermini";

const AU_LEARN_LINKS = [
  { href: "/performance", text: "Performance" },
  { href: "/technology", text: "Technology" },
  { href: "/why-mila", text: "Why Mila?" },
];

const AIRCYLOPEDIA_NAV = {
  href: "https://buy.milacares.com/blogs/home",
  text: "The Aircyclopedia™",
  submenu: false,
  external: true,
  openInNewTab: false,
};

const usesFullAccessoriesSidebar = (regional) =>
  regional === SUPPORTED_REGIONS.US ||
  regional === SUPPORTED_REGIONS.CA ||
  regional === SUPPORTED_REGIONS.GB;

const buildHeaderNavs = (regional, isMobile) => {
  const airNav = {
    href: "",
    text: `Air${isMobile ? " Series" : ""}`,
    submenu: true,
    menuKey: "air",
  };

  if (regional === SUPPORTED_REGIONS.AU) {
    return [
      airNav,
      { href: "/filters", text: "Filters", submenu: false },
      { href: "/app", text: "App", submenu: false },
      { href: "", text: "Learn", submenu: true, menuKey: "learn" },
      { href: "/our-story", text: "Our story", submenu: false },
    ];
  }

  return [
    airNav,
    { href: "/filters", text: "Filters", submenu: false },
    {
      href: "/performance",
      text: isMobile ? "Performance" : "Performance",
      submenu: false,
    },
    { href: "/technology", text: "Technology", submenu: false },
    regional === SUPPORTED_REGIONS.US || regional === SUPPORTED_REGIONS.GB
      ? AIRCYLOPEDIA_NAV
      : { href: "/our-story", text: "Our Story", submenu: false },
  ];
};

/**
 * @param pathname
 * @param topBarRef
 * @returns {JSX.Element}
 * @constructor
 */

const HeaderRefresh = ({
  pathname,
  topBarRef,
  headerRef,
  className,
  isOrder,
  setIsOrder,
  isMenuActive,
  setIsMenuActive,
  localization,
}) => {
  const dispatch = useDispatch();
  const { region } = localization;
  const regional = SUPPORTED_REGIONS[region] || SUPPORTED_REGIONS.US;
  const regionalPricesStatus = useSelector(selectRegionalPricesStatus);
  const [isSticky, setIsSticky] = useState(false);
  const [headerOffset, setHeaderOffset] = useState(0);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const isAu = regional === SUPPORTED_REGIONS.AU;
  const [navOffset, setNavOffset] = useState(0);
  const bannersStatus = useSelector(selectBannersStatus);
  const banners = useSelector(selectBannersData);
  const bannersError = useSelector(selectBannersError);
  const menuRef = useRef(null);
  const megaMenuRef = useRef(null);
  const observerRef = useRef(null);
  const orderRef = useRef(null);
  const orderBtnRef = useRef(null);

  const isMobile = useIsMobile(1025);

  useEffect(() => {
    if (regionalPricesStatus === STATUS.IDLE) {
      dispatch(fetchRegionalPrices(localization));
    }
  }, [dispatch, localization, regionalPricesStatus]);

  useLayoutEffect(() => {
    const updateOffsets = () => {
      const topbarHeight = topBarRef?.current?.offsetHeight || 0;
      const headerHeight = headerRef?.current?.offsetHeight || 0;

      setHeaderOffset(topbarHeight);
      setNavOffset(headerHeight);
    };

    updateOffsets();

    const timeout = setTimeout(updateOffsets, 500);
    observerRef.current = new ResizeObserver(updateOffsets);

    if (topBarRef?.current) {
      observerRef.current.observe(topBarRef.current);
    }
    if (headerRef?.current) {
      observerRef.current.observe(headerRef.current);
    }

    return () => {
      clearTimeout(timeout);
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [topBarRef, headerRef, bannersStatus, banners, bannersError, region]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        megaMenuRef.current &&
        !megaMenuRef.current.contains(event.target)
      ) {
        setActiveSubmenu(null);
      }

      if (
        orderRef.current &&
        !orderRef.current.contains(event.target) &&
        !orderBtnRef.current.contains(event.target)
      ) {
        setIsOrder(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const { body } = document;
    body.style.setProperty("--menuOffset", `${headerOffset + navOffset}px`);
  }, [headerOffset, navOffset]);

  const handleMenuItemClick = (text, event) => {
    event.preventDefault();
    setActiveSubmenu(activeSubmenu === text ? null : text);
  };

  const menuHandler = (e) => {
    e.preventDefault();
    setIsMenuActive(!isMenuActive);
    setIsOrder(false);
  };

  // const isHomePage = (pathname) => {
  //   const homePages = ["/", "/ca", "/us", "/ca/", "/us/"];
  //   return homePages.includes(pathname);
  // };

  const orderHandler = (e) => {
    e.preventDefault();
    setIsOrder(!isOrder);
    setIsMenuActive(false);
  };

  const handleMenuReset = () => {
    setIsMenuActive(false);
    if (!isMobile) {
      setActiveSubmenu(null);
    }
  };
  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 40);
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  const navs = buildHeaderNavs(regional, isMobile);
  const airNavText = navs[0].text;

  const navItems = navs.map(
    ({
      href,
      text,
      submenu,
      external,
      openInNewTab = true,
      menuKey = "air",
    }) => {
      return (
        <li className="header__menu-item" key={text}>
          {external ? (
            <a
              href={href}
              className={`${submenu ? " has-submenu" : ""}${
                activeSubmenu === text ? " header__menu-item--active" : ""
              }`}
              {...(openInNewTab
                ? { target: "_blank", rel: "noopener noreferrer" }
                : {})}
              onClick={handleMenuReset}
            >
              <span data-text={text}>{text}</span>
            </a>
          ) : (
            <Link
              to={href || "#"}
              className={`${href === pathname ? "header__menu-item--current" : ""}${
                submenu ? " has-submenu" : ""
              }${activeSubmenu === text ? " header__menu-item--active" : ""}`}
              onClick={(e) =>
                submenu ? handleMenuItemClick(text, e) : handleMenuReset()
              }
            >
              <span data-text={text}>{text}</span>
            </Link>
          )}
          {activeSubmenu === text &&
            isMobile &&
            submenu &&
            menuKey === "air" && (
              <MegaMenu
                ref={megaMenuRef}
                isMobile={isMobile}
                regional={regional}
              />
            )}
          {activeSubmenu === text &&
            isMobile &&
            submenu &&
            menuKey === "learn" && (
              <LearnMenu ref={megaMenuRef} pathname={pathname} />
            )}
        </li>
      );
    },
  );

  const isSubmenuOpen =
    !isMobile && (activeSubmenu === "Air" || activeSubmenu === "Learn");

  return (
    <header
      className={`header ${
        (isSubmenuOpen || isOrder) && !isMobile ? "submenu--active" : ""
      }${className} ${isSticky ? "header--sticky" : ""}`}
      ref={headerRef}
      style={{
        "--offsetTop": `${headerOffset}px`,
        "--menuOffset": `${headerOffset + navOffset}px`,
      }}
    >
      <div className="header__inner">
        <div className="container">
          <div className="header__grid">
            <button
              type="button"
              aria-label={isMenuActive ? "Close menu" : "Open menu"}
              aria-expanded={isMenuActive}
              className="header__menu-toggle"
              onClick={(e) => menuHandler(e)}
            >
              {!isMenuActive ? <MenuBar /> : <CloseBar />}
            </button>
            <div className="header__logo">
              <a href="/">
                <span className="screen-reader-text">Mila Air Homepage</span>
                <img src={logo} alt="Mila branding" loading="lazy" />
              </a>
            </div>

            <nav
              className={`header__nav${
                isMenuActive ? " header__nav--active" : ""
              }`}
              ref={menuRef}
            >
              <ul className="header__menu">
                {React.Children.toArray(navItems)}
              </ul>
              <a
                href="https://account.milacares.com"
                className="button button--md button--primary button--shadow-primary login-button"
              >
                Login to My Account
              </a>
            </nav>
            <div className="header__actions">
              <a
                href="https://account.milacares.com"
                className="button button--md button--white button--shadow-dark login-button--desktop"
              >
                {isAu ? "Login" : "Mila Account"}
              </a>
              <button
                type="button"
                className="button button--md button--primary button--shadow-primary button--order"
                onClick={(e) => orderHandler(e)}
                ref={orderBtnRef}
              >
                Buy
              </button>
            </div>
          </div>
        </div>
      </div>
      {activeSubmenu === airNavText && !isMobile && (
        <MegaMenu ref={megaMenuRef} isMobile={isMobile} regional={regional} />
      )}
      {activeSubmenu === "Learn" && !isMobile && (
        <LearnMenu ref={megaMenuRef} pathname={pathname} />
      )}
      {isOrder && <OrderNow regional={regional} ref={orderRef} />}
    </header>
  );
};

// const LoginButton = () => {
//   return (
//     <a
//       href="#"
//       className="button button--md button--white button--shadow-dark login-button"
//     >
//       Login
//     </a>
//   );
// };

const MegaMenu = React.forwardRef(({ regional, isMobile }, ref) => {
  const isAu = regional === SUPPORTED_REGIONS.AU;
  const currencySign = CURRENCY_SIGN[regional] || CURRENCY_SIGN.US;
  const currencyCode = CURRENCY_MAPPING[regional] || CURRENCY_MAPPING.US;
  const regionalPrices = useSelector(selectRegionalPricesData);
  const airPrice = getUnitPrice(
    regionalPrices,
    PRODUCT_VARIANT_ABBREVIATIONS.BASIC_BREATHER,
  );
  const airMiniPrice = getUnitPrice(
    regionalPrices,
    PRODUCT_VARIANT_ABBREVIATIONS.BASIC_BREATHER_MINI,
  );
  const air3BuyUrl = getAir3BuyUrl(regional);

  return (
    <div className="mega-menu" ref={ref}>
      <div className="mega-menu__grid">
        <div className="mega-menu__item">
          <div className="menu-product">
            <ImageLoader
              className="menu-product__image img-relative"
              placeholderAspectRatio={1 / 1}
              image={milaAir3}
              webp={milaAir3Webp}
              alt={"Mila Air"}
            />
            <div className="menu-product__content">
              {!isAu && <span className="mila-item__tag">New Year Deal</span>}
              <h3 className="menu-product__title">
                Mila Air <Air3Svg />
              </h3>
              <p className="menu-product__price">
                {`Starting at ${currencySign}${airPrice || ""} ${currencyCode}`}{" "}
                {airPrice?.del && <del>${airPrice?.del}</del>}
                <span>with Auto-Refill</span>
              </p>
              <div className="menu-product__description">
                <p>
                  Meet the newest — and toughest – member of the Mila fam, with
                  the power to handle larger spaces with ease.
                </p>
              </div>
              <a
                href={air3BuyUrl}
                className="button button--primary button--sm"
              >
                {getAir3MenuCta(regional, isMobile)}
              </a>
            </div>
          </div>
        </div>
        <div className="mega-menu__item">
          <div className="menu-product">
            <ImageLoader
              className="menu-product__image img-relative"
              placeholderAspectRatio={1 / 1}
              image={milaAirMini}
              webp={milaAirMiniWebp}
              alt={"Mila Air Mini"}
            />
            <div className="menu-product__content">
              {!isAu && <span className="mila-item__tag">New Year Deal</span>}
              <h3 className="menu-product__title">Mila Air Mini</h3>
              {isAu ? (
                <div className="menu-product__description">
                  <p>
                    Your favorite air purifier is now more accessible (and fun!)
                    than ever.
                  </p>
                </div>
              ) : (
                <>
                  <p className="menu-product__price">
                    {`Starting at ${currencySign}${airMiniPrice || ""} ${currencyCode}`}{" "}
                    {airMiniPrice?.del && <del>${airMiniPrice?.del}</del>}
                    <span>with Auto-Refill</span>
                  </p>
                  <div className="menu-product__description">
                    <p>
                      Your favorite air purifier is now more accessible (and
                      fun!) than ever.
                    </p>
                  </div>
                </>
              )}
              <a
                href={
                  isAu ? AIR_MINI_WAITLIST_URL : "https://buy.milacares.com/"
                }
                className="button button--primary button--sm"
              >
                {isAu ? "Join the Waitlist" : "Order Now"}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

const LearnMenu = React.forwardRef(({ pathname }, ref) => (
  <div className="mega-menu mega-menu--learn" ref={ref}>
    <div className="mega-menu__grid mega-menu__grid--learn">
      <ul className="mega-menu__learn-list">
        {AU_LEARN_LINKS.map(({ href, text }) => (
          <li key={href}>
            <Link
              to={href}
              className={
                href === pathname ? "mega-menu__learn-link--current" : ""
              }
            >
              {text}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  </div>
));

const OrderNow = React.forwardRef(({ regional }, ref) => {
  const isAu = regional === SUPPORTED_REGIONS.AU;
  const currencySign = CURRENCY_SIGN[regional] || CURRENCY_SIGN.US;
  const currencyCode = CURRENCY_MAPPING[regional] || CURRENCY_MAPPING.US;
  const regionalPrices = useSelector(selectRegionalPricesData);
  const airPrice = getUnitPrice(
    regionalPrices,
    PRODUCT_VARIANT_ABBREVIATIONS.BASIC_BREATHER,
  );
  const airMiniPrice = getUnitPrice(
    regionalPrices,
    PRODUCT_VARIANT_ABBREVIATIONS.BASIC_BREATHER_MINI,
  );
  const airFilterPrice = getFilterPrice(
    regionalPrices,
    PRODUCT_VARIANT_ABBREVIATIONS.BASIC_BREATHER,
  );
  const airMiniFilterPrice = getFilterPrice(
    regionalPrices,
    PRODUCT_VARIANT_ABBREVIATIONS.BASIC_BREATHER_MINI,
  );
  const airSocksPrice = getSocksPrice(
    regionalPrices,
    PRODUCT_VARIANT_ABBREVIATIONS.SOCKS,
  );
  const air3BuyUrl = getAir3BuyUrl(regional);
  const showBundlesCopy =
    regional === SUPPORTED_REGIONS.US || regional === SUPPORTED_REGIONS.CA;

  return (
    <div className="header__sidebar order-list" ref={ref}>
      <SidebarItem title="Choose your Mila">
        <div className="mila-list">
          <div className="mila-item">
            <ImageLoader
              className="mila-item__image img-relative"
              placeholderAspectRatio={1 / 1}
              image={milaAir3}
              webp={milaAir3Webp}
              alt={"Mila Air"}
            />
            <div className="mila-item__content">
              <div className="mila-item__heading">
                {!isAu && <span className="mila-item__tag">New Year Deal</span>}
                <h3 className="mila-item__title">
                  Mila Air <Air3Svg />
                </h3>
                <p className="mila-item__price">
                  {`Starting at ${currencySign}${airPrice || ""} ${currencyCode}`}{" "}
                  {airPrice?.del && <del>${airPrice?.del}</del>}
                  <span>
                    with Auto-Refill{showBundlesCopy ? " and bundles" : ""}
                  </span>
                </p>
              </div>
              <a
                href={air3BuyUrl}
                className="button button--primary button--sm"
              >
                Buy Now
              </a>
            </div>
          </div>
          {!isAu && (
            <div className="mila-item">
              <ImageLoader
                className="mila-item__image img-relative"
                placeholderAspectRatio={1 / 1}
                image={milaAirMini}
                webp={milaAirMiniWebp}
                alt={"Mila Air Mini"}
              />
              <div className="mila-item__content">
                <div className="mila-item__heading">
                  <span className="mila-item__tag">New Year Deal</span>
                  <h3 className="mila-item__title">Mila Air Mini</h3>
                  <p className="mila-item__price">
                    {`Starting at ${currencySign}${airMiniPrice || ""} ${currencyCode}`}{" "}
                    {airMiniPrice?.del && <del>${airMiniPrice?.del}</del>}
                    <span>
                      with Auto-Refill{showBundlesCopy ? " and bundles" : ""}
                    </span>
                  </p>
                </div>
                <a
                  href="https://buy.milacares.com/"
                  className="button button--primary button--sm"
                >
                  Buy Now
                </a>
              </div>
            </div>
          )}
        </div>
      </SidebarItem>

      <SidebarItem title="Buy accessories">
        <div className="mila-list">
          {usesFullAccessoriesSidebar(regional) ? (
            <>
              <AccessoriesAir3
                currencySign={currencySign}
                filterPrice={airFilterPrice}
                currencyCode={currencyCode}
              />
              <AccessoriesAirMini
                currencySign={currencySign}
                filterPrice={airMiniFilterPrice}
                currencyCode={currencyCode}
              />
            </>
          ) : (
            <>
              <div className="mila-item mila-item--filter">
                <ImageLoader
                  className="mila-item__image img-relative"
                  placeholderAspectRatio={1 / 1}
                  image={accessory01}
                  webp={accessory01Webp}
                  alt={"Mila Air Filters"}
                />
                <div className="mila-item__content">
                  <div className="mila-item__heading">
                    <h3 className="mila-item__title">Mila Air Filter</h3>
                    <p className="mila-item__price">
                      Starting at ${airFilterPrice}
                    </p>
                    {/* <button
                  type="button"
                  className="button button--white button--sm button--shadow-dark"
                >
                  Filter type <IconAngleDown />
                </button> */}
                  </div>
                  <a
                    href="https://shop.milacares.com/filters"
                    className="button button--primary button--sm"
                  >
                    Buy Now
                  </a>
                </div>
              </div>

              <div className="mila-item mila-item--filter">
                <ImageLoader
                  className="mila-item__image img-relative"
                  placeholderAspectRatio={1 / 1}
                  image={accessory03}
                  webp={accessory03Webp}
                  alt={"Mila Air Socks"}
                />
                <div className="mila-item__content">
                  <div className="mila-item__heading">
                    <h3 className="mila-item__title">Mila Socks (5 pack)</h3>
                    <p className="mila-item__price">${airSocksPrice}</p>
                  </div>
                  <a
                    href="https://shop.milacares.com/socks"
                    className="button button--primary button--sm"
                  >
                    Buy Now
                  </a>
                </div>
              </div>
            </>
          )}
        </div>
      </SidebarItem>
      <a
        href="https://account.milacares.com"
        className="button button--primary button--sm button--mila-account"
      >
        Mila Account
      </a>
    </div>
  );
});

const AccessoriesAir3 = ({ currencySign, filterPrice, currencyCode }) => (
  <div className="mila-item mila-item--filter">
    <ImageLoader
      className="mila-item__image img-relative"
      placeholderAspectRatio={1 / 1}
      image={accessory01}
      webp={accessory01Webp}
      alt={"Mila Air Filters"}
    />
    <div className="mila-item__content">
      <div className="mila-item__heading">
        <h3 className="mila-item__title">
          Mila Air <Air3Svg /> / Air Filters
        </h3>
        <p className="mila-item__price">
          {`Starting at ${currencySign}${filterPrice || ""} ${currencyCode}`}
        </p>
      </div>
      <a
        href="https://buy.milacares.com/collections/mila-filter?filter.p.product_type=Mila+Air%2FAir3"
        className="button button--primary button--sm"
      >
        Buy Now
      </a>
    </div>
  </div>
);
const AccessoriesAirMini = ({ currencySign, filterPrice, currencyCode }) => (
  <div className="mila-item mila-item--filter">
    <ImageLoader
      className="mila-item__image img-relative"
      placeholderAspectRatio={1 / 1}
      image={airminiAccessory}
      webp={airminiAccessoryWebp}
      alt={"Mila Air Mini"}
    />
    <div className="mila-item__content">
      <div className="mila-item__heading">
        <h3 className="mila-item__title">Mila Air Mini Filters</h3>
        <p className="mila-item__price">
          {`Starting at ${currencySign}${filterPrice || ""} ${currencyCode}`}
        </p>
      </div>
      <a
        href="https://buy.milacares.com/collections/mila-filter?filter.p.product_type=Mila+Air+Mini"
        className="button button--primary button--sm"
      >
        Buy Now
      </a>
    </div>
  </div>
);

const Air3Svg = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <rect x="0.5" y="0.5" width="18.5" height="18.5" rx="4" stroke="black" />
    <path
      d="M9.67151 14.818C6.88551 14.818 5.58351 13.53 5.49951 11.36H8.04751C8.13151 12.186 8.52351 12.746 9.51751 12.746C10.3995 12.746 10.8335 12.326 10.8335 11.612C10.8335 10.828 10.4135 10.436 9.51751 10.436H8.71951V8.658H9.46151C10.3575 8.658 10.7915 8.252 10.7915 7.552C10.7915 6.95 10.3995 6.558 9.64351 6.558C8.87351 6.558 8.42551 7.02 8.31351 7.832H5.77951C5.90551 5.858 7.22151 4.5 9.71351 4.5C12.0655 4.5 13.4795 5.578 13.4795 7.244C13.4795 8.364 12.8355 9.092 11.7855 9.4C12.9335 9.638 13.7315 10.45 13.7315 11.668C13.7315 13.572 12.3175 14.818 9.67151 14.818Z"
      fill="black"
    />
  </svg>
);

export default HeaderRefresh;
